import React, {
  ComponentType,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useStateContext } from '../../hooks/useStateContext';
import api from '../../services/api';
import { toaster } from '../../services/toaster';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import {
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  ListItem,
  ListItemAvatar,
  OutlinedInput,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import TagOutlinedIcon from '@mui/icons-material/TagOutlined';
import Avatar from '@mui/material/Avatar';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { debounce } from 'lodash';

function ImageIcon() {
  return null;
}

export const SlackSettings: ComponentType = () => {
  const { user } = useStateContext();
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [channels, setChannels] = useState<any>([]);
  const timer = React.useRef<any>();
  const [selectedChannel, setSelectedChannel] = React.useState<any>(undefined);
  const [messages, setMessages] = React.useState<any>([]);
  const [page, setPage] = React.useState<number>(1);
  const [hasMore, setHasMore] = React.useState<boolean>(true);
  const [scrollTop, setScrollTop] = React.useState<number>(0);
  const listRef = useRef<any>(null);
  const [searchValue, setSearchValue] = useState<string>('');

  useEffect(() => {
    document.title = `Zoe - Slack`;
  }, []);

  const debouncedOnChange = debounce(handleDebouncedOnChange, 500);
  const ref = useRef<any>();

  function handleDebouncedOnChange(value: any) {
    setSearchValue(value);
  }

  const handleScroll = () => {
    if (!hasMore) {
      return false;
    }

    if (listRef.current) {
      const { scrollTop, clientHeight, scrollHeight } = listRef.current;
      setScrollTop(scrollTop);

      if (scrollTop + clientHeight >= scrollHeight - 200) {
        setPage(page + 1);
      }
    }

    return true;
  };

  const downloadImage = (response: any, fileName: string) => {
    const imageBlob = new Blob([response], { type: 'image/png' });
    const imageUrl = URL.createObjectURL(imageBlob);
    const link: any = document.createElement('a');
    link.href = imageUrl;
    link.download = fileName ? fileName : 'image.png';
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  const handleDownload = (attachment: any) => {
    const searchParams = new URLSearchParams({
      url: attachment?.url,
    });
    api
      .getFile(`/download-slack-file`, searchParams)
      .then((res: any) => {
        downloadImage(res, attachment?.name);
      })
      .catch((error) => {
        console.log(error);
        toaster.show({
          icon: 'error',
          message: `Error while download data`,
          intent: 'danger',
        });
        setLoading(false);
      });

    return true;
  };

  useEffect(() => {
    setLoading(true);

    api
      .get(`/slack-channels`)
      .then((res: any) => {
        setChannels(res);
        setLoading(false);
      })
      .catch((error) => {
        toaster.show({
          icon: 'error',
          message: `Error while loading data`,
          intent: 'danger',
        });
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (selectedChannel) {
      setLoadingMessages(true);
      const searchParams = new URLSearchParams({
        paginateSize: '50',
        page: `${page}`,
        searchValue: searchValue as string,
      });

      api
        .get(`/slack-channels/${selectedChannel['id']}`, searchParams)
        .then((res: any) => {
          if (page >= res?.last_page) {
            setHasMore(false);
          }

          if (page === 1) {
            setMessages(res.data);
          } else {
            const newMessages = [...messages];
            res.data.map((row: any) => {
              newMessages.push(row);
            });
            setMessages(newMessages);
          }
        })
        .catch((error) => {
          toaster.show({
            icon: 'error',
            message: `Error while loading data`,
            intent: 'danger',
          });
        });
    }
  }, [selectedChannel, page, searchValue]);

  return (
    <>
    {/*  <Header
        changePage={() => {
          console.log();
        }}
      />*/}

      <div className={'p-[20px]'}>
        <Stack direction="row" spacing={2}>
          <Paper elevation={0} sx={{ width: '100%', maxWidth: 300 }}>
            {loading ? (
              <>
                <Skeleton
                  variant="rounded"
                  height={60}
                  sx={{
                    marginBottom: '5px',
                    width: '100%',
                  }}
                />
                <Skeleton
                  variant="rounded"
                  height={60}
                  sx={{
                    marginBottom: '5px',
                    width: '100%',
                  }}
                />
                <Skeleton
                  variant="rounded"
                  height={60}
                  sx={{
                    marginBottom: '5px',
                    width: '100%',
                  }}
                />
                <Skeleton
                  variant="rounded"
                  height={60}
                  sx={{
                    marginBottom: '5px',
                    width: '100%',
                  }}
                />
                <Skeleton
                  variant="rounded"
                  height={60}
                  sx={{
                    marginBottom: '5px',
                    width: '100%',
                  }}
                />
                <Skeleton
                  variant="rounded"
                  height={60}
                  sx={{
                    marginBottom: '5px',
                    width: '100%',
                  }}
                />
                <Skeleton
                  variant="rounded"
                  height={60}
                  sx={{
                    marginBottom: '5px',
                    width: '100%',
                  }}
                />
                <Skeleton
                  variant="rounded"
                  height={60}
                  sx={{
                    marginBottom: '5px',
                    width: '100%',
                  }}
                />
                <Skeleton
                  variant="rounded"
                  height={60}
                  sx={{
                    marginBottom: '5px',
                    width: '100%',
                  }}
                />
                <Skeleton
                  variant="rounded"
                  height={60}
                  sx={{
                    marginBottom: '5px',
                    width: '100%',
                  }}
                />
              </>
            ) : (
              <nav aria-label="main mailbox folders">
                {channels.length > 0 && (
                  <>
                    <List>
                      {channels.map((channel: any, index: number) => {
                        return (
                          <>
                            <ListItem
                              disablePadding
                              key={index}
                              sx={{
                                '& .active': {
                                  background: '#e0eeff',
                                },
                                '& .MuiListItemButton-root': {
                                  paddingY: '0px',
                                },
                                '& .MuiListItemIcon-root': {
                                  minWidth: '36px',
                                },
                              }}
                            >
                              <ListItemButton
                                onClick={() => {
                                  if (ref.current) {
                                    ref.current.value = '';
                                  }

                                  setSelectedChannel(channel);
                                  setPage(1);
                                  setSearchValue('');
                                }}
                                className={`${
                                  channel?.id === selectedChannel?.id
                                    ? 'active'
                                    : ''
                                }`}
                              >
                                <ListItemIcon>
                                  {channel?.type === 'public_channel' ? (
                                    <TagOutlinedIcon />
                                  ) : (
                                    <LockOutlinedIcon />
                                  )}
                                </ListItemIcon>
                                <ListItemText primary={`${channel?.name}`} />
                              </ListItemButton>
                            </ListItem>
                          </>
                        );
                      })}
                    </List>
                  </>
                )}
              </nav>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: 'calc(100% - 300px)',
              height: '100%',
            }}
          >
            <Stack
              sx={{
                padding: '20px',
              }}
              direction="row"
              spacing={2}
            >
              <FormControl fullWidth variant="outlined">
                <InputLabel htmlFor="outlined-adornment-search">
                  Search
                </InputLabel>
                <OutlinedInput
                  id="outlined-adornment-search"
                  inputRef={ref}
                  type={'text'}
                  endAdornment={
                    <InputAdornment position="end">
                      <SearchOutlinedIcon />
                    </InputAdornment>
                  }
                  label="Search"
                  onChange={(event) => {
                    debouncedOnChange(event.target.value);
                  }}
                />
              </FormControl>
            </Stack>

            <List
              sx={{
                margin: '20px',
                background: '#f9f9f9',
                boxShadow: 'inset 0px 0px 7px -1px #00000038',
                overflowY: 'auto',
                maxHeight: 'calc(100vh - 270px)',
              }}
              className={'rounded-[10px]'}
              onScroll={() => {
                handleScroll();
              }}
              ref={listRef}
            >
              {messages.map((message: any) => {
                return (
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar>
                        {message?.slack_user?.nickname
                          .substring(0, 1)
                          .toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <React.Fragment>
                          <Typography
                            sx={{ display: 'inline' }}
                            component="span"
                            variant="body2"
                            color="text.primary"
                          >
                            {message?.slack_user?.nickname}
                          </Typography>
                          <Typography
                            component="small"
                            sx={{
                              color: '#666',
                              fontSize: '14px',
                            }}
                          >
                            {` - ${message?.stamp}`}
                          </Typography>
                        </React.Fragment>
                      }
                      secondary={
                        <React.Fragment>
                          <span className={'[&>a]:text-[#1c63d5]'}>
                            TESZT
                          </span>
                          <Stack direction="row" spacing={1}>
                            {message?.slack_files.map((attachment: any) => {
                              return (
                                <Chip
                                  label={attachment?.name}
                                  onClick={() => {
                                    handleDownload(attachment);
                                  }}
                                />
                              );
                            })}
                          </Stack>
                        </React.Fragment>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        </Stack>
      </div>
    </>
  );
};
